import { useEffect, useRef } from "react";

import { useTranslator } from "@eo-locale/react";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { Box, IconButton, MdSort } from "@/storybook-components";

import PopoverAction from "@/components/tools/common/popover";
import useClickOutsideHandler from "@/hooks/useClickOutsideHandler";
import { setCurrentPageNumber } from "@/redux/features/place-marker/slice";
import { setFilteredCategoriesData } from "@/redux/features/place-markers/slice";
import { fetchPinDetails } from "@/redux/features/place-markers/thunk";

import {
  clearAccessibilityFn,
  upDownArrowAccessibility
} from "@/helpers/up-down-arrow-accessibility";

const SortPins = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector((state) => state.project.project.id);
  const placesId = useAppSelector((state) => state.place.place.id);
  const { translate } = useTranslator();
  const { id: userId } = useAppSelector(
    (state) => state.user.registeredUser.profile
  );

  const sortTypeList = ["newest", "topPins"];
  const { filteredCategoriesData } = useAppSelector(
    (state) => state.placeMarkersSlice
  );
  const {
    categories,
    isSelfPin,
    sortOrder: sortValue
  } = filteredCategoriesData;

  const sortRef = useRef(null);
  const { handleActive } = useClickOutsideHandler(sortRef);

  // for accessibility
  useEffect(() => {
    if (!handleActive) clearAccessibilityFn();
  }, [handleActive]);

  const fetchSortedList = (sort_method, order) => {
    if (isSelfPin && userId) {
      dispatch(
        fetchPinDetails({
          projectId,
          placesId,
          filters: {
            sort: {
              [sort_method]: order
            },
            user_id: userId,
            category_id: categories
          }
        })
      );
    } else {
      dispatch(
        fetchPinDetails({
          projectId,
          placesId,
          filters: { sort: { [sort_method]: order }, category_id: categories }
        })
      );
    }
  };

  const handleClick = async (sortType: string) => {
    dispatch(
      setFilteredCategoriesData({
        ...filteredCategoriesData,
        sortOrder: sortType
      })
    );
    dispatch(setCurrentPageNumber(1));
    switch (sortType) {
      case "newest":
        return fetchSortedList("created_at", "desc");
      case "topPins":
        return fetchSortedList("votes_count", "desc");
    }
  };

  return (
    <PopoverAction
      content={
        <Box
          py={2}
          sx={{
            "& > div": {
              color: "gray.800",
              padding: "0.8rem",
              "&:hover": {
                backgroundColor: "gray.100",
                cursor: "pointer"
              }
            }
          }}
        >
          {sortTypeList.map((value, index) => {
            return (
              <Box
                bg={sortValue === value ? "gray.100" : "white"}
                key={index}
                tabIndex={0}
                onClick={() => handleClick(value)}
                onKeyPress={(e) => {
                  if (e.code === "Enter" || e.code === "Space") {
                    handleClick(value);
                  }
                }}
                className="sort-item"
              >
                {translate(`places.sortPins.${value}`)}
              </Box>
            );
          })}
        </Box>
      }
      triggerIcon={
        <IconButton
          ref={sortRef}
          aria-label={translate("places.sortBy")}
          icon={<MdSort />}
          size="lg"
          variant="solid"
          borderRadius="50%"
          color="gray.900"
          boxShadow="lg"
          bg={handleActive ? "gray.300" : "white"}
          _hover={
            handleActive
              ? { bg: "gray.300", color: "gray.900" }
              : { bg: "white", color: "gray.900" }
          }
          _focus={{
            bg: "white",
            color: "gray.900"
          }}
          onClick={() => upDownArrowAccessibility("sort-item", "sort-button")}
          className="sort-button"
        />
      }
      borderColor="gray.200"
      maxW={56}
    />
  );
};

export default SortPins;
